import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { capitalCallPendencyAPI, fundAPI } from '../../services/api';
import { AlertTriangle, ArrowLeft, FileSpreadsheet, Loader2, ChevronRight, ChevronDown } from 'lucide-react';

const CapitalCallPendency = () => {
  const [funds, setFunds] = useState([]);
  const [fundFilter, setFundFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');

  // -------------------------------------------------------------------
  // Drill-down state. `expanded` is a Set of "accountId|fundId" keys so
  // multiple rows can be open at once. `breakdowns` caches loaded rows
  // (keyed the same way) — refetched only if the aggregate list reloads
  // (fund filter change). Prevents flicker + saves round-trips.
  // -------------------------------------------------------------------
  const [expanded, setExpanded] = useState(new Set());
  const [breakdowns, setBreakdowns] = useState({});  // key → { loading, error, data }

  const rowKey = (r) => `${r.account_id}|${r.fund_id}`;

  const toggleRow = async (r) => {
    const k = rowKey(r);
    const next = new Set(expanded);
    if (next.has(k)) {
      next.delete(k);
      setExpanded(next);
      return;
    }
    next.add(k);
    setExpanded(next);
    // Only load the breakdown on the FIRST open — cached thereafter.
    if (!breakdowns[k]) {
      setBreakdowns(prev => ({ ...prev, [k]: { loading: true } }));
      try {
        const res = await capitalCallPendencyAPI.breakdown(r.account_id, r.fund_id);
        setBreakdowns(prev => ({ ...prev, [k]: { loading: false, data: res.data } }));
      } catch (e) {
        setBreakdowns(prev => ({
          ...prev,
          [k]: { loading: false, error: e?.response?.data?.detail || 'Failed to load breakdown' },
        }));
      }
    }
  };

  useEffect(() => {
    fundAPI.getAll().then(res => setFunds(res.data)).catch(() => setFunds([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Fund filter changed — invalidate any previously-opened drill-downs
    // so stale data can't leak across contexts.
    setExpanded(new Set());
    setBreakdowns({});
    capitalCallPendencyAPI.get(fundFilter || undefined)
      .then(res => {
        if (cancelled) return;
        setRows(res.data?.rows || []);
        setTotalPending(res.data?.total_pending || 0);
      })
      .catch(() => { if (!cancelled) { setRows([]); setTotalPending(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fundFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await capitalCallPendencyAPI.export(fundFilter || undefined);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'capital_call_pendency.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to export'); }
    finally { setExporting(false); }
  };

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.account_name || '').toLowerCase().includes(q)
      || (r.fund_name || '').toLowerCase().includes(q)
      || (r.pan || '').toLowerCase().includes(q);
  });

  const totals = filtered.reduce((acc, r) => {
    acc.called += r.called || 0;
    acc.received += r.received || 0;
    acc.pending += r.pending || 0;
    return acc;
  }, { called: 0, received: 0, pending: 0 });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="capital-call-pendency-page">
      <Link to="/investors/reports" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to Reports
      </Link>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Capital Call Pendency</h1>
          <p className="text-sm text-gray-500 mt-1">Investor accounts where Amount Called &gt; Amount Received, grouped by fund.</p>
        </div>
        <button onClick={handleExport} disabled={exporting || filtered.length === 0}
          className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
          data-testid="export-pendency-btn">
          {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5" />}
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={fundFilter} onChange={e => setFundFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="pendency-fund-filter">
          <option value="">All Funds</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by investor name, PAN, or fund..." data-testid="pendency-search"
          className="flex-1 min-w-[240px] border rounded-lg px-3 py-2 text-sm" />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Rows with Pendency</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{filtered.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Called</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{totals.called.toLocaleString()}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Received</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{totals.received.toLocaleString()}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Pending</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="total-pending">{totals.pending.toLocaleString()}</p>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500">Loading pendency report...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <AlertTriangle size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No outstanding capital calls. Either no drawdowns have been issued or every called amount has been received.</p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                <th className="px-4 py-3">Investor Account</th>
                <th className="px-4 py-3">PAN</th>
                <th className="px-4 py-3">Fund</th>
                <th className="px-4 py-3 text-center">Ccy</th>
                <th className="px-4 py-3 text-right">Called</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3 text-right">Pending</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const k = rowKey(r);
                const isOpen = expanded.has(k);
                const bd = breakdowns[k];
                return (
                  <React.Fragment key={`${r.account_id}-${r.fund_id}-${i}`}>
                    <tr
                      className={`border-b hover:bg-blue-50 cursor-pointer ${isOpen ? 'bg-blue-50/50' : ''}`}
                      onClick={() => toggleRow(r)}
                      data-testid={`pendency-row-${r.account_id}-${r.fund_id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="inline-flex items-center gap-1.5">
                          {isOpen
                            ? <ChevronDown size={14} className="text-blue-600" />
                            : <ChevronRight size={14} className="text-gray-400" />}
                          <span className="hover:text-blue-600">{r.account_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.pan || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{r.fund_name}</td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">{r.currency || '-'}</td>
                      <td className="px-4 py-3 text-right text-blue-700">{(r.called || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-green-700">{(r.received || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-red-600 font-semibold">{(r.pending || 0).toLocaleString()}</td>
                    </tr>

                    {isOpen && (
                      <tr
                        className="border-b bg-gray-50/60"
                        data-testid={`pendency-breakdown-${r.account_id}-${r.fund_id}`}
                      >
                        <td colSpan={7} className="px-4 py-4">
                          {bd?.loading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Loader2 size={14} className="animate-spin" />
                              Loading drawdown-wise breakdown…
                            </div>
                          ) : bd?.error ? (
                            <div className="text-sm text-red-600">{bd.error}</div>
                          ) : bd?.data?.rows?.length ? (
                            <div className="rounded-md border border-gray-200 bg-white overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-100 text-gray-600 text-left">
                                    <th className="px-3 py-2 font-semibold">Drawdown</th>
                                    <th className="px-3 py-2 font-semibold">Class</th>
                                    <th className="px-3 py-2 font-semibold text-right">Called</th>
                                    <th className="px-3 py-2 font-semibold text-right">Received</th>
                                    <th className="px-3 py-2 font-semibold text-right">Pending</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bd.data.rows.map((br, brIdx) => (
                                    <tr
                                      key={`${br.drawdown_id}-${br.class || 'noclass'}-${brIdx}`}
                                      className="border-t hover:bg-gray-50"
                                      data-testid={`breakdown-row-${br.drawdown_id}-${brIdx}`}
                                    >
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900">{br.drawdown_name}</div>
                                        <div className="text-[11px] text-gray-500 font-mono">{br.drawdown_number}</div>
                                      </td>
                                      <td className="px-3 py-2 text-gray-700">{br.class}</td>
                                      <td className="px-3 py-2 text-right text-blue-700">{(br.called || 0).toLocaleString()}</td>
                                      <td className="px-3 py-2 text-right text-green-700">{(br.received || 0).toLocaleString()}</td>
                                      <td className={`px-3 py-2 text-right font-semibold ${(br.pending || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                        {(br.pending || 0).toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {bd.data.totals && (
                                  <tfoot>
                                    <tr className="bg-gray-50 border-t font-semibold">
                                      <td colSpan={2} className="px-3 py-2">Total</td>
                                      <td className="px-3 py-2 text-right text-blue-700">{(bd.data.totals.called || 0).toLocaleString()}</td>
                                      <td className="px-3 py-2 text-right text-green-700">{(bd.data.totals.received || 0).toLocaleString()}</td>
                                      <td className="px-3 py-2 text-right text-red-600">{(bd.data.totals.pending || 0).toLocaleString()}</td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 italic">
                              No drawdown-level rows found for this pair.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t font-semibold">
                <td className="px-4 py-3" colSpan={4}>Total</td>
                <td className="px-4 py-3 text-right text-blue-700">{totals.called.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-green-700">{totals.received.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-red-600">{totals.pending.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default CapitalCallPendency;
